import React, { useState, useEffect, useMemo } from "react";
import './TauriSearchResult.css';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import Nav from 'react-bootstrap/Nav';
import Tab from 'react-bootstrap/Tab';
import { useLocation } from 'react-router-dom';
import config from '../../appURL.json';
import TauriItemCard from "./TauriItemCard/TauriItemCard";
import TauriLoader from "../TauriLoader/TauriLoader";
import TauriSelectedModuleLabel from "./TauriSelectedModuleLabel/TauriSelectedModuleLabel";
import TauriCustomPaganation from "../TauriCustomPaganation/TauriCustomPaganation";
import TauriSearchResultDetailModal from "../TauriSearchResultDetailModal/TauriSearchResultDetailModal";

const TauriSearchResult = () => {
    const location = useLocation();

    // Memoize the searchPayload to avoid unnecessary re-renders
    const { searchPayload, searchConfig } = useMemo(() => location.state, [location.state]);

    const [condition, setCondition] = useState(searchPayload?.Condition || "Contains");
    const [items, setItems] = useState([]);
    const [activeTab, setActiveTab] = useState(''); // State to track active tab
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1); // State to track current page
    const [displayPerPage, setDisplayPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1); // State to track total pages
    const [totalItems, setTotalItems] = useState(0);
    const [pageTransactionIDs, setPageTransactionIDs] = useState({});

    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    const [searchText, setSearchText] = useState(null);
    const [searchCatalogs, setSearchCatalogs] = useState(null);

    useEffect(() => {
        setCurrentPage(1);
        setPageTransactionIDs({});
    }, [searchPayload.Text])

    useEffect(() => {
        fetchAPISearch();
    }, [searchPayload.Text, searchPayload.Catalogs, currentPage, displayPerPage, condition]); 

    useEffect(() => {
        const firstGroup = Object.keys(groupedItems)[0];
        setActiveTab(firstGroup || ''); // Set the first group as default tab
    }, [items]); // This runs when items change

    const fetchAPISearch = async () => {
        const token = localStorage.getItem('token');
        try {
            setLoading(true);
            const transactionIDForPage = pageTransactionIDs[currentPage] || null;
            setSearchText(searchPayload.Text);
            setSearchCatalogs(searchPayload.Catalogs.toString());
            const updatedSearchPayload = {
                ...searchPayload,
                PageIndex: currentPage,
                ResultsPerPage: displayPerPage,
                Condition: condition,
                TransactionID: searchText == searchPayload.Text && searchCatalogs == searchPayload.Catalogs.toString() ? transactionIDForPage : null
            };

            const response = await fetch(config.API_Search, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'x-session-key': 12345
                },
                body: JSON.stringify(updatedSearchPayload),
            });

            if (!response.ok) {
                console.error('Failed to fetch data:', response.status);
                return;
            }

            const data = await response.json();
            setItems(data.items);
            setTotalPages(data.page_count);
            setTotalItems(data.total_items);

            if (data.transaction_id !== 0) {
                setPageTransactionIDs(prev => ({
                    ...prev,
                    [currentPage]: data.transaction_id
                }));
            }

        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false); // Hide the loader
        }
    };

    const handleSelect = (eventKey) => {
        setCondition(eventKey);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber); // Update current page when pagination is clicked
    };

    const handleDisplayPerPage = (displayPerPage) => {
        setDisplayPerPage(displayPerPage);
        setCurrentPage(1);
    };

    // Group items by their "group" value
    const groupedItems = items.reduce((acc, item) => {
        if (!acc[item.group]) {
            acc[item.group] = [];
        }
        acc[item.group].push(item);
        return acc;
    }, {});

    const handleItemCardClick = (item) => {
        setSelectedItem(item);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedItem(null);
    };

    return (
        <div className="tauri-searchresult">
            <div className="result-header">
                Search Results for: "<span className="highlighted-text">{searchPayload.Text}</span>"
                <Button className="change-button">
                    Change
                </Button>
            </div>
            <div className="result-condition d-none d-lg-flex flex-column flex-lg-row align-items-start">
                <div className="search-condition d-flex align-items-center">
                    <div className="me-2">Search Condition: </div>
                    <Dropdown onSelect={handleSelect}>
                        <Dropdown.Toggle>
                            {condition}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            {searchConfig?.conditions && searchConfig.conditions.length > 0 ? (
                                searchConfig.conditions.map(({ id, title, description }, index) => (
                                    <React.Fragment key={id}>
                                        <Dropdown.Item eventKey={id}>
                                            <div className="dropdown-item-title">{title}</div>
                                            <div className="dropdown-item-description">{description}</div>
                                        </Dropdown.Item>
                                        {index < searchConfig.conditions.length - 1 && <hr className="custom-hr" />}
                                    </React.Fragment>
                                ))
                            ) : (
                                <Dropdown.Item disabled>
                                    No Data Available
                                </Dropdown.Item>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>

                <div className="resultselected-module ms-md-3">
                    <TauriSelectedModuleLabel searchTerm={searchPayload.Text} catalogIds={searchPayload.Catalogs} sections={searchConfig.sections} />
                    <img src={process.env.PUBLIC_URL + "/Images/carbon--settings.svg"} className="resultmodule-image" alt="Icon"/>
                </div>
            </div>

            {loading ? (
                <TauriLoader borderSize={'0.1875rem'} widthSize={'30px'} heightSize={'30px'} />
            ) : (
                <>
                    <Tab.Container activeKey={activeTab} onSelect={(key) => setActiveTab(key)}>
                        <Nav variant="tabs">
                            {Object.keys(groupedItems).map(group => (
                                <Nav.Item key={group}>
                                    <Nav.Link eventKey={group}>
                                        {groupedItems[group][0].group}
                                        <span className="badge">{groupedItems[group].length}</span>
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                        <Tab.Content>
                            {Object.keys(groupedItems).map(group => (
                                <Tab.Pane eventKey={group} key={group}>
                                    {groupedItems[group].map((item, index) => (
                                        <TauriItemCard key={index} item={item} onClick={() => handleItemCardClick(item)} />
                                    ))}
                                </Tab.Pane>
                            ))}
                        </Tab.Content>
                    </Tab.Container>
                    {Object.keys(groupedItems).length > 0 &&
                        <TauriCustomPaganation
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            displayPerPage={displayPerPage}
                            onDisplayPerPage={handleDisplayPerPage}
                            totalItems={totalItems}
                        />
                    }
                </>
            )}

            <TauriSearchResultDetailModal
                show={showModal}
                onHide={handleCloseModal}
                item={selectedItem}
            />
        </div>
    );
}

export default TauriSearchResult;
